import type { OpenerGroup } from '../../api/useGroups';

/** Shown in place of an account id the group does not carry. */
export const GROUP_ACCOUNT_UNSET = 'none set';

export interface GroupAccountPair {
  /** `kalshiAccountId · polymarketAccountId`, with unset legs spelled out. */
  label: string;
  /**
   * Either leg has no account. Such a group cannot be started — the opener
   * refuses it — so the chip says so instead of rendering a plausible pair.
   */
  incomplete: boolean;
}

/**
 * The venue accounts a group trades on, as the header chip renders them.
 *
 * The ids are printed VERBATIM and never abbreviated to their `-main` / `-test`
 * suffix: accounts are registry rows, so a third account can share a suffix
 * with a second, and the whole point of the account model is that an operator
 * never has to infer which book a group is arming.
 *
 * Returns `null` when there is no group to describe — the caller renders
 * nothing rather than a chip about an absent group.
 */
export function groupAccountPair(
  group:
    | Pick<OpenerGroup, 'kalshiAccountId' | 'polymarketAccountId'>
    | null
    | undefined
): GroupAccountPair | null {
  if (!group) return null;
  const kalshi = group.kalshiAccountId?.trim() || null;
  const polymarket = group.polymarketAccountId?.trim() || null;
  return {
    label: `${kalshi ?? GROUP_ACCOUNT_UNSET} · ${
      polymarket ?? GROUP_ACCOUNT_UNSET
    }`,
    incomplete: kalshi === null || polymarket === null
  };
}
